import { SessionProvider } from "next-auth/react"
import { GrowthBookProviderWrapper } from "components/growthbook-provider"
import GrowthBookPageViewTracker from "components/tracking/GrowthBookPageViewTracker"
import { GoogleTagManager } from "@next/third-parties/google"
import { Provider } from "src/components/ui/provider.js"
import { CacheProvider } from "@emotion/react"
import createCache from "@emotion/cache"
import Head from "next/head"
import makeTheme from "styles/theme"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { DFPSlotsProvider } from "react-dfp"
import { GlobalStateProvider } from "providers/globalState"
import sites from "config/sites.mjs"

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // always use the cached response on the same page
            staleTime: Infinity,
            networkMode: "always",
        },
    },
})

const defaultEmotionCache = createCache({
    key: "emotion-css-cache",
    prepend: true, // ensures styles are prepended to the <head>, instead of appended
})

// global stylesheet here
import "styles/globals.css"
import PageGuard from "components/auth/PageGuard"
import ScrollToHash from "components/nav/ScrollToHash"

const Noop = ({ children }) => <>{children}</>

function MyApp({ Component, pageProps: { session, ...pageProps }, emotionCache }) {
    const PageContextProvider = Component.provider || Noop

    // TODO: these defaults are set here to suppress a build error on pages that do not receive the theme: is there a better solution?
    let themeDefault = pageProps.site?.theme || {}
    if (!themeDefault || !themeDefault.colorPalette?.length) {
        themeDefault.colorPalette = [
            {
                color1: "#333",
                color2: "#333",
                color3: "#333",
                color4: "#333",
                color5: "#333",
                color6: "#333",
                color7: "#333",
                color8: "#333",
                color9: "#333",
                color10: "#333",
                color11: "#333",
                color12: "#333",
                color13: "#333",
                color14: "#333",
                color15: "#333",
                color16: "#333",
            },
        ]
    }

    const customTheme = makeTheme(themeDefault)

    // Use SSR emotion cache if provided, otherwise use default
    const clientSideEmotionCache = emotionCache || defaultEmotionCache

    const siteConfig = Object.values(sites).find(site => site.craftId === pageProps.site?.meeting?.siteId)

    // construct JSONLD data
    const siteName = pageProps.site?.meeting?.meetingName
    const siteUrl = siteConfig?.fullDomain
        ? `https://${siteConfig.fullDomain}/`
        : `https://${pageProps.locale}.crfconferences.com/`
    const websiteJsonLd = siteName
        ? JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: siteName,
              url: siteUrl,
          })
        : null

    return (
        <CacheProvider value={clientSideEmotionCache}>
            <GrowthBookProviderWrapper>
                <GrowthBookPageViewTracker />
                <Provider theme={customTheme}>
                    <GoogleTagManager gtmId="GTM-PWDG2H7" />
                    <Head>
                        <link
                            rel="stylesheet"
                            href={`/styles/meetings/${pageProps.locale?.replace("-archive", "")}.css`}
                        />
                        {siteConfig?.isArchived && <meta name="robots" content="noindex, nofollow" />}
                        {websiteJsonLd && (
                            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: websiteJsonLd }} />
                        )}
                    </Head>
                    <SessionProvider session={session}>
                        <GlobalStateProvider
                            locale={pageProps.locale}
                            site={pageProps.site}
                            page={{
                                ...pageProps.page,
                                section: pageProps.section,
                            }}
                        >
                            <DFPSlotsProvider
                                dfpNetworkId={pageProps.site?.meeting.googleAdsNetworkId || "14164860"}
                                lazyLoad={true}
                                sizeMapping={[
                                    {
                                        viewport: [728, 490],
                                        sizes: [
                                            [900, 300],
                                            [970, 90],
                                            [728, 210],
                                            [728, 90],
                                            [240, 400],
                                        ],
                                    },
                                    {
                                        viewport: [0, 0],
                                        sizes: [
                                            [350, 44],
                                            [320, 100],
                                            [320, 50],
                                            [240, 400],
                                        ],
                                    },
                                ]}
                            >
                                <QueryClientProvider client={queryClient}>
                                    <PageContextProvider>
                                        <PageGuard pageStatus={pageProps.pageStatus}>
                                            <Component {...pageProps} />
                                        </PageGuard>
                                    </PageContextProvider>
                                    <ReactQueryDevtools initialIsOpen={false} />
                                </QueryClientProvider>
                            </DFPSlotsProvider>
                        </GlobalStateProvider>
                    </SessionProvider>
                    <ScrollToHash />
                </Provider>
            </GrowthBookProviderWrapper>
        </CacheProvider>
    )
}

export default MyApp
